<template>
  <div
    role="main"
    aria-label="Détails d’une localité logistique"
    tabindex="-1"
  >
    <b-card
      v-if="locality"
      no-body
      aria-label="Carte de détails de la localité"
    >
      <b-card-header>
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <h1
              id="locality-title"
              class="card-title"
            >
              {{ locality.name }}
              <small
                v-if="locality.zip_code"
                class="text-muted ml-2"
              >({{ locality.zip_code }})</small>
            </h1>
            <span class="sr-only">Détails complets de la localité pour la gestion logistique professionnelle.</span>
          </div>
          <div class="ml-5">
            <b-button
              v-if="user.role === 'admin'"
              :variant="locality.is_active ? 'outline-danger' : 'outline-success'"
              :aria-label="(locality.is_active ? 'Désactiver' : 'Activer') + ' la localité'"
              @click="toggleStatus"
            >
              {{ locality.is_active ? 'Désactiver' : 'Activer' }}
            </b-button>
            <b-badge
              :variant="locality.is_active ? 'danger' : 'success'"
              class="ml-2"
              :aria-label="'Statut de la localité : ' + locality.active_status"
            >
              {{ locality.active_status }}
            </b-badge>
          </div>
        </div>
      </b-card-header>

      <b-card-body>
        <b-tabs
          id="locality-tabs"
          v-model="activeTab"
          content-class="mt-3"
          aria-label="Navigation des sections de la localité"
        >
          <!-- Information Tab -->
          <b-tab
            title="Information"
            active
            aria-label="Informations générales sur la localité"
          >
            <b-row>
              <b-col
                lg="5"
                md="6"
                sm="12"
              >
                <b-card title="Information">
                  <b-list-group flush>
                    <b-list-group-item aria-label="Nom de la localité">
                      <strong>Nom :</strong> {{ locality.name }}
                    </b-list-group-item>
                    <b-list-group-item aria-label="Code géographique de la localité">
                      <strong>Code géographique :</strong> {{ locality.code || 'Non défini' }}
                    </b-list-group-item>
                    <b-list-group-item aria-label="Code postal de la localité">
                      <strong>Code postal :</strong> {{ locality.zip_code || 'Non défini' }}
                    </b-list-group-item>
                    <b-list-group-item aria-label="Délégation logistique">
                      <strong>Délégation :</strong>
                      <router-link
                        v-if="locality.delegation"
                        :to="{ name: 'delegation-detail', params: { id: locality.delegation.id }}"
                        :aria-label="'Voir la délégation ' + locality.delegation.name"
                      >
                        {{ locality.delegation.name }}
                      </router-link>
                      <span v-else>Non défini</span>
                    </b-list-group-item>
                    <b-list-group-item aria-label="Gouvernorat logistique">
                      <strong>Gouvernorat :</strong>
                      <router-link
                        v-if="locality.governorate"
                        :to="{ name: 'governorate-detail', params: { id: locality.governorate.id }}"
                        :aria-label="'Voir le gouvernorat ' + locality.governorate.name"
                      >
                        {{ locality.governorate.name }}
                      </router-link>
                      <span v-else>Non défini</span>
                    </b-list-group-item>
                    <b-list-group-item aria-label="Nombre de commandes logistiques">
                      <strong>Nombre de commandes :</strong> {{ locality.num_orders || 0 }}
                    </b-list-group-item>
                    <b-list-group-item aria-label="Statut de la localité">
                      <strong>État :</strong> {{ locality.active_status }}
                    </b-list-group-item>
                  </b-list-group>
                </b-card>
              </b-col>
              <b-col
                lg="7"
                md="6"
                sm="12"
              >
                <b-card title="Statistiques de commandes">
                  <div
                    v-if="locality.num_orders > 0"
                    class="text-center mb-3"
                  >
                    <h2
                      class="display-4"
                      aria-label="Nombre total de commandes"
                    >
                      {{ locality.num_orders }}
                    </h2>
                    <p class="lead">
                      Commandes totales
                    </p>
                  </div>
                  <div
                    v-else
                    class="text-center py-5"
                  >
                    <p aria-live="polite">
                      Aucune commande n'a été trouvée pour cette localité
                    </p>
                  </div>
                </b-card>
              </b-col>
            </b-row>
          </b-tab>

          <!-- Orders Tab -->
          <b-tab
            title="Commandes"
            aria-label="Commandes associées à la localité"
          >
            <b-card title="Toutes les commandes">
              <b-row>
                <b-col
                  cols="12"
                  class="mb-2"
                >
                  <b-form-group
                    label="Filtres"
                    aria-label="Filtres de recherche de commandes"
                    label-cols-sm="3"
                    label-align-sm="right"
                    label-size="sm"
                    class="mb-0"
                  >
                    <b-input-group size="sm">
                      <b-form-input
                        v-model="ordersFilter"
                        placeholder="Rechercher par référence ou client"
                        aria-label="Champ de recherche de commandes par référence ou client"
                      />
                      <b-input-group-append>
                        <b-button
                          :disabled="!ordersFilter"
                          @click="ordersFilter = ''"
                        >
                          Effacer
                        </b-button>
                      </b-input-group-append>
                    </b-input-group>
                  </b-form-group>
                </b-col>
                <b-col cols="12">
                  <b-table
                    :items="filteredOrders"
                    :fields="orderFields"
                    :busy="ordersLoading"
                    striped
                    hover
                    responsive
                    show-empty
                    empty-text="Aucune commande trouvée"
                    :per-page="perPage"
                    :current-page="currentPage"
                    aria-label="Tableau des commandes associées à la localité"
                  >
                    <template #cell(reference)="data">
                      <router-link :to="{ name: 'order-show', params: { id: data.item.reference } }">
                        {{ data.item.reference }}
                      </router-link>
                    </template>

                    <template #cell(status)="data">
                      <b-badge :variant="getStatusBadgeVariant(data.item.status)">
                        {{ data.item.status }}
                      </b-badge>
                    </template>

                    <template #cell(amount)="data">
                      {{ formatPrice(data.item.amount) }}
                    </template>

                    <template #cell(actions)="data">
                      <router-link :to="{ name: 'order-show', params: { id: data.item.reference } }">
                        <feather-icon
                          icon="EyeIcon"
                          class="cursor-pointer"
                          size="16"
                        />
                      </router-link>
                    </template>
                  </b-table>

                  <!-- Pagination -->
                  <div class="mt-3">
                    <b-pagination
                      v-model="currentPage"
                      :total-rows="rows"
                      :per-page="perPage"
                      align="center"
                      aria-controls="orders-table"
                      class="my-0"
                      aria-label="Pagination des commandes"
                    />
                  </div>
                </b-col>
              </b-row>
            </b-card>
          </b-tab>
        </b-tabs>

        <!-- Show only top 5 recent orders on the information tab -->
        <orders-table
          v-if="locality.recent_orders && locality.recent_orders.length > 0"
          :orders="locality.recent_orders"
          :total-orders="locality.num_orders"
          :entity-id="locality.id"
          entity-type="locality"
          card-title="Dernières commandes"
          class="mt-3"
          view-all-text="Voir l'onglet Commandes"
          @view-all-orders="viewAllOrders"
        />

        <b-row class="mt-2">
          <b-col cols="12">
            <b-button
              v-if="isAdmin"
              variant="primary"
              class="mr-1"
              :to="{ name: 'localities-edit', params: { id: $route.params.id }}"
              aria-label="Modifier la localité"
            >
              <feather-icon
                icon="EditIcon"
                class="mr-50"
                size="16"
              />
              <span>Modifier</span>
            </b-button>
            <b-button
              variant="outline-primary"
              :to="{ name: 'localities-list' }"
              aria-label="Retour à la liste des localités"
            >
              <feather-icon
                icon="ListIcon"
                class="mr-50"
                size="16"
              />
              <span>Liste des localités</span>
            </b-button>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Modal for all orders -->
    <b-modal
      id="locality-orders-modal"
      title="Toutes les commandes"
      size="xl"
      hide-footer
    >
      <b-table
        :items="allOrders"
        :fields="orderFields"
        :busy="ordersLoading"
        striped
        hover
        responsive
      >
        <template #cell(reference)="data">
          <router-link :to="{ name: 'order-show', params: { id: data.item.reference } }">
            {{ data.item.reference }}
          </router-link>
        </template>

        <template #cell(status)="data">
          <b-badge :variant="getStatusBadgeVariant(data.item.status)">
            {{ data.item.status }}
          </b-badge>
        </template>

        <template #cell(amount)="data">
          {{ formatPrice(data.item.amount) }}
        </template>

        <template #cell(actions)="data">
          <router-link :to="{ name: 'order-show', params: { id: data.item.reference } }">
            <feather-icon
              icon="EyeIcon"
              class="cursor-pointer"
              size="16"
            />
          </router-link>
        </template>
      </b-table>

      <!-- Pagination component -->
      <div class="mt-3">
        <b-pagination
          v-model="currentPage"
          :total-rows="rows"
          :per-page="perPage"
          align="center"
          aria-controls="orders-table"
          class="my-0"
        />
      </div>

      <template #modal-footer>
        <b-button
          variant="secondary"
          @click="$bvModal.hide('locality-orders-modal')"
        >
          Fermer
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios'
import OrdersTable from '@/components/tables/OrdersTable.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import storeAuth from '@/store/store'
import {
  BCard, BCardHeader, BCardBody, BRow, BCol, BListGroup, BListGroupItem,
  BBadge, BButton, BTabs, BTab, BTable, BPagination, BFormGroup,
  BInputGroup, BFormInput, BInputGroupAppend, BModal,
} from 'bootstrap-vue'

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BListGroup,
    BListGroupItem,
    BBadge,
    BButton,
    OrdersTable,
    BTabs,
    BTab,
    BTable,
    BPagination,
    BFormGroup,
    BInputGroup,
    BFormInput,
    BInputGroupAppend,
    BModal,
  },
  data() {
    return {
      locality: null,
      isLoading: true,
      user: {},
      orderFields: [
        { key: 'reference', label: 'Référence' },
        { key: 'created_at', label: 'Date' },
        { key: 'client_name', label: 'Client' },
        { key: 'amount', label: 'Montant' },
        { key: 'status', label: 'État' },
        { key: 'actions', label: 'Actions' },
      ],
      allOrders: [],
      ordersLoading: false,
      ordersFilter: '',
      currentPage: 1,
      perPage: 10,
      activeTab: 0,
    }
  },

  computed: {
    isAdmin() {
      return this.user.role === 'admin'
    },

    filteredOrders() {
      if (!this.ordersFilter) {
        return this.allOrders
      }

      const filter = this.ordersFilter.toLowerCase()
      return this.allOrders.filter(order => order.reference.toString().includes(filter)
               || order.client_name.toLowerCase().includes(filter))
    },

    rows() {
      return this.filteredOrders.length
    },
  },
  created() {
    this.user = storeAuth.state.user
    this.fetchLocalityDetails()
  },
  methods: {
    async fetchLocalityDetails() {
      this.isLoading = true
      try {
        const { id } = this.$route.params

        const { data } = await axios.get(`/api/localities/detail/${id}/`)
        this.locality = data

        // After setting this.locality
        if (this.locality && this.locality.id) {
          this.fetchAllOrders()
        }
      } catch (error) {
        console.error('Error fetching locality details:', error)

        if (error.response && error.response.status === 404) {
          this.showToast('danger', 'top-center', `La localité avec l'ID ${this.$route.params.id} n'existe pas`)
        } else {
          this.showToast('danger', 'top-center', 'Erreur lors du chargement des détails de la localité')
        }

        // Redirect to the localities list after a brief delay
        setTimeout(() => {
          this.$router.push({ name: 'localities-list' })
        }, 3000)
      } finally {
        this.isLoading = false
      }
    },

    async fetchAllOrders() {
      try {
        this.ordersLoading = true
        const { data } = await axios.get('/api/orders/list/', {
          params: {
            locality: this.$route.params.id,
            page_size: 100, // Adjust based on expected number of orders
          },
        })

        // Transform orders into the format expected by the table
        this.allOrders = data.results.map(order => ({
          reference: order.id,
          client_name: order.name,
          status: this.getOrderStatusText(order.status),
          amount: parseFloat(order.price_ttc),
          created_at: new Date(order.created_at).toLocaleDateString(),
        }))
      } catch (error) {
        console.error('Error fetching orders for locality:', error)
        this.showToast('danger', 'top-center', 'Erreur lors du chargement des commandes')
      } finally {
        this.ordersLoading = false
      }
    },

    getOrderStatusText(statusCode) {
      const statusMap = {
        0: 'En Attente',
        1: 'En Pickup',
        2: 'Colis au dépôt',
        3: 'En cours',
        4: 'Livré',
        5: 'Retour au dépôt',
        6: 'Livré payé',
        7: 'Retour expéditeur',
        8: 'En Transfert',
        9: 'Retour payé',
      }
      return statusMap[statusCode] || 'Inconnu'
    },

    getStatusBadgeVariant(status) {
      const variants = {
        'En attente': 'warning',
        Livré: 'success',
        Retourné: 'danger',
        'En cours': 'info',
        'Colis au dépôt': 'secondary',
      }
      return variants[status] || 'secondary'
    },

    showToast(variant, position, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Notification',
          icon: 'InfoIcon',
          text,
          variant,
        },
      }, {
        position,
      })
    },

    async toggleStatus() {
      try {
        // Show confirmation dialog before proceeding
        const result = await this.$swal({
          title: this.locality.is_active ? 'Désactiver la localité?' : 'Activer la localité?',
          text: this.locality.is_active
            ? 'Cette action empêchera les livraisons dans cette localité. Êtes-vous sûr?'
            : 'Cette action permettra les livraisons dans cette localité. Êtes-vous sûr?',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Oui, confirmer',
          cancelButtonText: 'Annuler',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        })

        // If user confirmed the action
        if (result.value) {
          const { data } = await axios.post(`/api/localities/toggle-status/${this.$route.params.id}/`)
          this.locality.is_active = data.is_active
          this.showToast('success', 'top-center',
            `La localité a été ${data.is_active ? 'activée' : 'désactivée'} avec succès`)
        }
      } catch (error) {
        console.error('Error toggling locality status:', error)
        this.showToast('danger', 'top-center', 'Erreur lors du changement de statut')
      }
    },

    viewAllOrders(event) {
      // Prevent default navigation
      if (event) {
        event.preventDefault()
      }

      // Switch to Orders tab
      this.activeTab = 1

      // Make sure the tab change takes effect
      this.$nextTick(() => {
        // Force tab to be active
        const tabsElement = document.getElementById('locality-tabs')
        if (tabsElement) {
          this.$bvTab.show('locality-tabs-1')
        }
      })
    },

    // Remove or keep the unused modal method
    showOrdersModal() {
      this.$bvModal.show('locality-orders-modal')
      if (!this.allOrders.length) {
        this.fetchAllOrders()
      }
    },

    switchToOrdersTab() {
      this.activeTab = 1 // Set the active tab to the Orders tab
    },

    formatPrice(price) {
      return new Intl.NumberFormat('fr-TN', {
        style: 'currency',
        currency: 'TND',
      }).format(price)
    },
  },
}
</script>

<style lang="scss" scoped>
.card-title {
  small {
    font-size: 0.65em;
  }
}

.table th, .table td {
  vertical-align: middle;
  font-size: 0.95rem;
}

.table thead th {
  position: sticky;
  top: 0;
  z-index: 10;
}

.b-pagination {
  justify-content: center;
}
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0,0,0,0);
  border: 0;
}
</style>
